import { useEffect } from 'react'

import { 
  autoDeriveVariant,
  isValidCssColor,
  normalizeCssColorToken,
} from '../../../shared/utils/colorUtils.js'
import { BREAKPOINTS_CSS_VARS } from '../../../shared/constants/breakpoints'

import {
  COLOR_DERIVE_CONTEXTS,
  COLORS_TO_DERIVE,
  DERIVE_VARIANTS,
  DEFAULT_STYLE
} from '../constants/defaultStyle'
import {
  RADIUS_THEME_NAMES,
  getRadiusThemeTokens,
} from '../constants/themes'

function resolveCssColorValue(...candidates) {
  for (const candidate of candidates) {
    const normalized = normalizeCssColorToken(candidate)
    if (isValidCssColor(normalized)) return normalized
  }
  return null
}

/**
 * Injects project theme configuration as CSS custom properties on the app root.
 *
 * Injected properties:
 * - Breakpoints:        --bim-breakpoint-{name}            (static, from shared/constants/breakpoints.js)
 * - Colors:             --bim-color-{name}                 (from style.colors, snake_case → kebab-case)
 * - Derived colors:     --bim-color-{name}-{suffix}        (auto-generated per COLOR_DERIVE_CONTEXTS)
 * - Status colors:      --bim-color-status-{name}          (from style.status_colors)
 * - Rooms colors:       --bim-color-rooms-{name}           (from style.rooms_colors)
 * - Insolation colors:  --bim-color-insolation-{0..10}     (from style.insolation_colors)
 * - Zone colors:        --bim-color-zone-{name}            (from style.zone_colors)
 * - Type colors:        --bim-color-type-{name}            (from style.type_colors)
 * - Compare colors:     --bim-color-compare-{p0..p9}       (from style.compare_colors)
 * - Brightness colors:  --bim-color-brightness-{name}      (from style.brightness_colors)
 * - Plan colors:        --bim-plan-{name}                  (from plan_colors, overridable via panel-styles.json)
 * - Hero outline:       --hero-outline-*                   (stroke width tokens)
 * - Fonts:              --bim-font-{name}                  (from style.fonts, excluding url)
 * - Google Fonts:       <link data-bim-dynamic-fonts-url>   (deduped in <head> per style.fonts.url)
 * - Radius theme:       --bim-radius-* / --bim-shape-*     (shape tokens per theme: rectangular/rounded/pill)
 *
 * Priority order for overridable families:
 *   panelStyles.{family} > projectConfig.style.{family} > DEFAULT_STYLE.{family}
 *
 * Priority order for radius_theme:
 *   panelStyles.radius_theme > projectConfig.style.radius_theme > RECTANGULAR (default)
 *
 * Runs once when projectStyle / panelStyles become available.
 * Re-runs only if references change (i.e. config reloads).
 *
 * @param {object|null} projectStyle - projectConfig.style object
 * @param {object|null} panelStyles  - panel-styles.json object (CMS design token overrides)
 * @param {React.RefObject<HTMLElement>|null} targetRef - app-root ref that owns runtime theme tokens
 */

/**
 * Injects all color CSS custom properties driven by COLOR_DERIVE_CONTEXTS.
 *
 * For solo colors (in COLORS_TO_DERIVE): injects derived variant suffixes only.
 * Base vars for solo colors are injected by the main DEFAULT_STYLE.colors loop.
 *
 * For color families: injects base var per entry + derived variant suffixes
 * per each declared context.
 *
 * CSS var naming:
 *   solo color    → --bim-color-{key}-{suffix}
 *   family entry  → --bim-color-{familyName}-{entry}          (base)
 *                 → --bim-color-{familyName}-{entry}-{suffix}  (derived)
 *
 * Priority for family entries:
 *   panelStyles.{family}?.[key] > projectStyle.{family}?.[key] > DEFAULT_STYLE.{family}?.[key]
 *
 * @param {HTMLElement}  root
 * @param {object|null}  projectStyle
 * @param {object|null}  panelStyles
 */
function injectAllColorVars(root, projectStyle, panelStyles) {
  for (const [colorKey, contexts] of Object.entries(COLOR_DERIVE_CONTEXTS)) {
    if (COLORS_TO_DERIVE.includes(colorKey)) {
      // ── Solo color — value lives in DEFAULT_STYLE.colors ──────────────────────
      const cssName = colorKey.replaceAll('_', '-')
      const base    = resolveCssColorValue(
        panelStyles?.colors?.[colorKey],
        projectStyle?.colors?.[colorKey],
        DEFAULT_STYLE.colors?.[colorKey],
      )
      if (!isValidCssColor(base)) continue

      for (const ctx of contexts) {
        for (const { suffix, type, ...params } of DERIVE_VARIANTS[ctx]) {
          const derivedKey     = `${colorKey}_${suffix.replace(/-/g, '_')}`
          const explicitOverride = resolveCssColorValue(
            panelStyles?.colors?.[derivedKey],
            projectStyle?.colors?.[derivedKey],
            DEFAULT_STYLE.colors?.[derivedKey],
          )
          const value = explicitOverride ?? autoDeriveVariant(base, type, params)
          if (value) root.style.setProperty(`--bim-color-${cssName}-${suffix}`, value)
        }
      }
    } else {
      // ── Color family — value lives in DEFAULT_STYLE[colorKey] ────────────────
      const familyName = colorKey.replace('_colors', '')
      const defaultMap = DEFAULT_STYLE[colorKey]
      if (!defaultMap || typeof defaultMap !== 'object') continue

      for (const [entryKey, defaultValue] of Object.entries(defaultMap)) {
        const cssKey  = String(entryKey).replaceAll('_', '-')
        const baseRaw =
          panelStyles?.[colorKey]?.[entryKey] ??
          projectStyle?.[colorKey]?.[entryKey] ??
          defaultValue
        const base = normalizeCssColorToken(baseRaw)
        if (!isValidCssColor(base)) continue

        // Base var — always
        root.style.setProperty(`--bim-color-${familyName}-${cssKey}`, base)

        // Derived variants per context
        for (const ctx of contexts) {
          for (const { suffix, type, ...params } of DERIVE_VARIANTS[ctx]) {
            const derivedKey     = `${familyName}_${entryKey}_${suffix.replace(/-/g, '_')}`
            const explicitOverride = resolveCssColorValue(
              panelStyles?.colors?.[derivedKey],
              projectStyle?.colors?.[derivedKey],
              DEFAULT_STYLE.colors?.[derivedKey],
            )
            const value = explicitOverride ?? autoDeriveVariant(base, type, params)
            if (value) root.style.setProperty(`--bim-color-${familyName}-${cssKey}-${suffix}`, value)
          }
        }
      }
    }
  }
}


// Hero outline colors are auto-derived from hero_outlines / hero_active (COLORS_TO_DERIVE)
// → --bim-color-hero-outlines-*, --bim-color-hero-active-*
// Only stroke widths are static.
function injectHeroOutlineVars(root) {
  root.style.setProperty('--hero-outline-stroke-width', '2px')
  root.style.setProperty('--hero-outline-stroke-width-active', '0.25em')
}

/**
 * Injects radius theme tokens based on selected profile (rectangular/rounded/pill).
 * Settles both shape scale (--bim-radius-*) and semantic tokens (--bim-shape-*).
 * 
 * Priority: panelStyles.radius_theme > projectStyle.radius_theme > RECTANGULAR (default)
 * 
 * @param {CSSStyleDeclaration} root
 * @param {object|null} projectStyle
 * @param {object|null} panelStyles
 */
function injectRadiusThemeTokens(root, projectStyle, panelStyles) {
  // Resolve radius theme with priority
  const radiusTheme =
    panelStyles?.radius_theme ||
    projectStyle?.radius_theme ||
    RADIUS_THEME_NAMES.RECTANGULAR

  // Validate theme name, fallback to rectangular if invalid
  const validTheme = Object.values(RADIUS_THEME_NAMES).includes(radiusTheme)
    ? radiusTheme
    : RADIUS_THEME_NAMES.RECTANGULAR

  // Get token set for this theme
  const tokenSet = getRadiusThemeTokens(validTheme)

  // Inject all tokens on the current app root
  for (const [cssVar, value] of Object.entries(tokenSet)) {
    root.style.setProperty(cssVar, value)
  }
}

/**
 * Injects a flat color family as CSS custom properties without derived variants.
 * Used for plan_colors, color_scheme_colors, and insolation_colors.
 *
 * Priority: panelStyles.{family} > projectStyle.{family} > DEFAULT_STYLE.{family}
 *
 * CSS variable name: --bim-{cssPrefix}-{kebab-key}
 * e.g. plan_colors.wall_partition    → --bim-plan-wall-partition
 *      zone_colors.day               → --bim-color-zone-day
 *      compare_colors.p0             → --bim-color-compare-p0
 *      insolation_colors.3           → --bim-color-insolation-3
 *
 * @param {CSSStyleDeclaration} root
 * @param {string} family       - key in DEFAULT_STYLE (e.g. 'plan_colors')
 * @param {string} cssPrefix    - prefix for CSS variable, without leading '--bim-'
 * @param {object|null} projectStyle
 * @param {object|null} panelStyles
 */
function injectDirectColorFamily(root, family, cssPrefix, projectStyle, panelStyles) {
  const defaults = DEFAULT_STYLE[family]
  if (!defaults) return

  for (const [key, defaultValue] of Object.entries(defaults)) {
    const cssKey = String(key).replaceAll('_', '-')
    const override =
      panelStyles?.[family]?.[key] ??
      projectStyle?.[family]?.[key] ??
      defaultValue

    if (override) {
      root.style.setProperty(`--bim-${cssPrefix}-${cssKey}`, override)
    }
  }
}

function ensureDynamicFontLink(url) {
  const existingLinks = document.querySelectorAll('link[data-bim-dynamic-fonts-url]')
  const existingLink = [...existingLinks].find(
    (candidate) => candidate.getAttribute('data-bim-dynamic-fonts-url') === url,
  )

  if (existingLink) return existingLink

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.setAttribute('data-bim-dynamic-fonts-url', url)
  document.head.appendChild(link)
  return link
}

export function useThemeVars(projectStyle, panelStyles = null, targetRef = null) {
  useEffect(() => {
    const root = targetRef?.current
    if (!projectStyle || !root) return

    // Breakpoints — static, same across all projects
    for (const [prop, value] of Object.entries(BREAKPOINTS_CSS_VARS)) {
      root.style.setProperty(prop, value)
    }

    // Colors → --bim-color-{kebab-case}
    if (DEFAULT_STYLE.colors) {
      for (const [key, fallback] of Object.entries(DEFAULT_STYLE.colors)) {
        const cssValue = resolveCssColorValue(
          panelStyles?.colors?.[key],
          projectStyle?.colors?.[key],
          fallback,
        )
        if (!isValidCssColor(cssValue)) continue
        root.style.setProperty(`--bim-color-${key.replaceAll('_', '-')}`, cssValue)
      }
    }

    // Derived variants for all color families + solo colors (per COLOR_DERIVE_CONTEXTS)
    injectAllColorVars(root, projectStyle, panelStyles)

    // Plan structural + room tokens → --bim-plan-{key}
    injectDirectColorFamily(root, 'plan_colors', 'plan', projectStyle, panelStyles)

    // Stroke widths only — colors come from auto-derived --bim-color-hero-outlines-* / hero-active-*
    injectHeroOutlineVars(root)

    // Radius theme tokens → --bim-radius-* / --bim-shape-*
    injectRadiusThemeTokens(root, projectStyle, panelStyles)

    // Fonts → --bim-font-{name} (url excluded — handled separately as <link>)
    const fontConfig = {
      ...DEFAULT_STYLE.fonts,
      ...projectStyle?.fonts,
      ...panelStyles?.fonts,
    }

    if (Object.keys(fontConfig).length > 0) {
      const { url, ...fontFamilies } = fontConfig
      for (const [key, value] of Object.entries(fontFamilies)) {
        if (!value) continue
        root.style.setProperty(`--bim-font-${key.replaceAll('_', '-')}`, value)
      }

      // Google Fonts live in <head>, but links are deduped per URL instead of a single global node.
      if (url) {
        const link = ensureDynamicFontLink(url)
        link.href = url
      }
    }
  }, [projectStyle, panelStyles, targetRef])
}
